/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { Component } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import { MobileSlideUpModal, colors } from '@blend/consumer-components';

import { InputWrapperProps } from '../../TaskInputs/types';
import AddressInputForm from './AddressInputForm';
import DummyAddressInput from './DummyAddressInput';
import AddressInputFieldsContainer from './AddressInputFieldsContainer';
import AddressAutocompleteDropdown, {
  OnAddressSelectedOpts,
} from './AddressAutocompleteDropdown/AddressAutocompleteDropdown';
import { AddressInput } from './types';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { isEmpty, last } from '@utils/lodash';

/**
 *
 * NOTE! Please be aware that it's likely that you'll have to update DesktopAddressInputWrapper along with this file.
 * They're fairly similar in behavior, but styling and functionality is different enough that it's better to keep
 * two separate wrapper components for desktop and mobile.
 *
 */

export interface OwnProps extends InputWrapperProps {
  display: string;
  existingAddresses: AddressInput.Address[];
  withCounty: boolean;
  locale?: AddressInput.Locale;
  isCityOnlyInput?: boolean;
}

interface OwnState {
  potentiallyInvalidAddress: AddressInput.Address | {};
  modalOpen: boolean;
  showSearch: boolean;
}

const styles = StyleSheet.create({
  buttonWrapper: {
    marginTop: 'auto',
  },
  contentWrapper: {
    height: '100%',
    padding: '0 15px 30px',
  },
  addressFormContentsWrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  // We put this top margin specifically on the dropdownWrapper because there
  // are different rules for margin collapsing between mobile safari and chrome,
  // so if we put it on the shared parent of both the address inputs and the dropdown
  // (contentWrapper), then the dropdown is positioned correctly but the address inputs
  // end up misaligned.
  dropdownWrapper: {
    marginTop: 25,
  },
});

class MobileAddressInputWrapper extends Component<OwnProps & TimerProps, OwnState> {
  constructor(props: any) {
    super(props);
    this.state = {
      modalOpen: false,
      showSearch: true,
      potentiallyInvalidAddress: props.input.value || {},
    };
  }

  getSearchInputName = () => {
    const { input } = this.props;
    return `${input.name}-search`;
  };

  onAddressChosenFromDropdown = (address: AddressInput.Address, opts: OnAddressSelectedOpts = { skipModal: false }) => {
    const { isCityOnlyInput } = this.props;
    // This is guaranteed to be a valid address, since we populated it from a currently existing known
    // valid address; in this scenario, we can skip the modal.
    if (opts.skipModal || isCityOnlyInput) {
      return this.onAddressSubmitSuccess(address);
    }

    // Otherwise, we need to open the modal for validation and populate it with the chosen address.
    this.setState({
      potentiallyInvalidAddress: address,
      showSearch: false,
    });
  };

  getClosestValidAddress = () => {
    const { input } = this.props;
    const localAddress = this.state.potentiallyInvalidAddress;
    const closestValidAddress = isEmpty(localAddress) ? input.value : localAddress;
    return closestValidAddress;
  };

  onAddressSubmitSuccess = (address: AddressInput.Address) => {
    const { input } = this.props;
    if (address) {
      input.onChange(address);
      // After the address has been submitted, we want to set the state up such that if the user clicks back into
      // the component, they get effectively a fresh input.
      this.setState({ modalOpen: false, showSearch: true, potentiallyInvalidAddress: address });
    }
  };

  handleDummyInputClick = () => {
    const { reactSafeSetTimeout } = this.props;
    this.setState({ modalOpen: true });
    // Important that the following focus() call is in the same callstack as the click on the dummy input, or
    // mobile phones won't let you do it since they generally don't want to let developers programmatically
    // focus inputs on behalf of users.
    // We need to defer the call to give the element time to be added to the document. Putting this logic in the
    // setState callback causes it to run before the autoCompleteInput is added to the page.
    reactSafeSetTimeout(() => {
      const searchInputName = this.getSearchInputName();
      const autoCompleteInput = last(document.getElementsByName(searchInputName));
      // istanbul ignore if
      if (!autoCompleteInput) {
        return;
        // return diagnostics.error(new Error(`Failed to find element ${searchInputName}`), {
        //   additionalMeta: 'No element found',
        // });
      }
      autoCompleteInput.focus();
    }, 0);
  };

  handleForceCloseModal = () => {
    // Reset the state to be completely fresh.
    this.setState({ modalOpen: false, potentiallyInvalidAddress: {}, showSearch: true });
  };

  render() {
    const { input, meta, display, existingAddresses, withCounty, locale, disabled, isCityOnlyInput } = this.props;
    const headerText = `Confirm ${display}`;
    const error = meta.touched ? meta.error : undefined;

    return (
      <div className="MobileAddressInputWrapper">
        <DummyAddressInput
          name={`${input.name}-dummy`}
          error={error}
          label={display}
          value={this.getClosestValidAddress()}
          onClick={this.handleDummyInputClick}
        />
        <MobileSlideUpModal
          isOpen={this.state.modalOpen}
          handleClose={this.handleForceCloseModal}
          closeText=""
          headerText={headerText}
          contentLabel={headerText}
          color={colors.black}
        >
          <div className={css(styles.contentWrapper)}>
            {this.state.showSearch && (
              <form className={css(styles.dropdownWrapper)}>
                <AddressAutocompleteDropdown
                  withCounty={withCounty}
                  showSearchIcon
                  onSelect={this.onAddressChosenFromDropdown}
                  label={display}
                  placeholder="Enter your address..."
                  name={this.getSearchInputName()}
                  error={error}
                  selectedValue={this.getClosestValidAddress()}
                  existingAddresses={existingAddresses}
                  disabled={disabled}
                  isCityOnlyInput={isCityOnlyInput}
                />
              </form>
            )}
            {!this.state.showSearch && (
              <AddressInputForm
                initialValues={this.state.potentiallyInvalidAddress}
                onSubmitSuccess={this.onAddressSubmitSuccess}
              >
                <div className={css(styles.addressFormContentsWrapper)}>
                  <AddressInputFieldsContainer withCounty={withCounty} locale={locale} />
                  <div className={css(styles.buttonWrapper)}>
                    <ActionButton data-dd-privacy="allow" type="submit">
                      Save
                    </ActionButton>
                  </div>
                </div>
              </AddressInputForm>
            )}
          </div>
        </MobileSlideUpModal>
      </div>
    );
  }
}

export default (props: OwnProps) => (
  <ProvideTimers render={timerProps => <MobileAddressInputWrapper {...timerProps} {...props} />} />
);
